<template>
  <div class="bcc-pagination-container">
    <ul class="bcc-pagination" v-if="!needHide">
      <li
        v-if="showPreviousButton"
        :class="previousClass"
        @click="handleClickPrevious">{{ previousText }}</li><li :class="item.classList"
        v-for="item in pageList"
        :key="item.page"
        @click="handleClickPage(item)"><a>{{ item.isEllipsis ? '⋯' : item.page }}</a></li><li
        v-if="showNextButton"
        :class="nextClass"
        @click="handleClickNext">{{ nextText }}</li><li v-if="showTotal || showElevator" class="bcc-pagination-extra">
          <div v-if="showTotal" :class="totalClass">
            {{totalText}}{{innerTotalPage}}{{totalPageText}} / {{total}}{{totalUnitText}}{{ showElevator ? totalSeparatorText : '' }}
          </div>
          <div v-if="showElevator" :class="elevatorClass">
            {{jumpToText}}
            <input type="text" v-model="elevatorValue"
              autocomplete="off"
              spellcheck="false"
              :disabled="disabled"
              @keyup.enter="handleJumpToPage" >
            {{jumpToPageText}}
          </div>
        </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'BccPagination',
  props: {
    current: {
      type: Number,
      default: 1
    },
    total: {
      type: Number,
      default: 0
    },
    totalPage: {
      type: Number,
      default: 0
    },
    pageSize: {
      type: Number,
      default: 10
    },
    displayPageCount: {
      type: Number,
      default: 5,
      validator (value) {
        return value > 0
      }
    },
    showTotal: {
      type: Boolean,
      default: false
    },
    showElevator: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    autoHide: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      innerCurrent: this.current,
      innerTotalPage: this.total ? Math.ceil(this.total / this.pageSize) : this.totalPage,
      elevatorValue: ''
    }
  },
  computed: {
    previousText () {
      return '上一页'
    },
    nextText () {
      return '下一页'
    },
    totalText () {
      return '共'
    },
    totalPageText () {
      return '页'
    },
    totalUnitText () {
      return '个'
    },
    totalSeparatorText () {
      return '，'
    },
    jumpToText () {
      return '跳转至'
    },
    jumpToPageText () {
      return '页'
    },
    showPreviousButton () {
      return this.innerCurrent > 1
    },
    showNextButton () {
      return this.innerCurrent < this.innerTotalPage
    },
    pageList () {
      let pageList = []
      // 首页
      pageList.push(this.getPageItem(1))
      // 中间页
      let leftDisplayCount = Math.floor((this.displayPageCount - 1) / 2)
      let rightDisplayCount = Math.floor(this.displayPageCount / 2)
      // 左省略号
      if (this.innerCurrent - leftDisplayCount > 2) {
        pageList.push(this.getPageItem(-1, true))
      }
      // 中间页
      if (this.innerTotalPage > 2) {
        let startPage = Math.max(2, this.innerCurrent - leftDisplayCount)
        let endPage = Math.min(this.innerTotalPage - 1, this.innerCurrent + rightDisplayCount)
        for (let i = startPage; i <= endPage; i++) {
          pageList.push(this.getPageItem(i))
        }
      }
      // 右省略号
      if (this.innerCurrent + rightDisplayCount < this.innerTotalPage - 1) {
        pageList.push(this.getPageItem(-2, true))
      }
      // 尾页
      if (this.innerTotalPage > 1) {
        pageList.push(this.getPageItem(this.innerTotalPage))
      }
      return pageList
    },
    previousClass () {
      return [
        'bcc-pagination-item',
        'bcc-pagination-previous',
        {
          'disabled': this.disabled || this.innerCurrent <= 1
        }
      ]
    },
    nextClass () {
      return [
        'bcc-pagination-item',
        'bcc-pagination-next',
        {
          'disabled': this.disabled || this.innerCurrent >= this.innerTotalPage
        }
      ]
    },
    totalClass () {
      return [
        'bcc-pagination-total',
        {
          'disabled': this.disabled
        }
      ]
    },
    elevatorClass () {
      return [
        'bcc-pagination-elevator',
        {
          'disabled': this.disabled
        }
      ]
    },
    needHide () {
      return this.autoHide && this.innerTotalPage <= 1
    }
  },
  watch: {
    current (val) {
      this.innerCurrent = val
    },
    total (val) {
      this.innerTotalPage = Math.ceil(val / this.pageSize)
      if (this.innerTotalPage < this.innerCurrent) {
        this.innerCurrent = this.innerTotalPage === 0 ? 1 : this.innerTotalPage
      }
    },
    totalPage (val) {
      this.innerTotalPage = val
    }
  },
  methods: {
    getPageItem (page = 1, ellipsis) {
      let classList = []
      if (ellipsis) {
        classList.push('bcc-pagination-ellipsis')
      } else {
        classList.push('bcc-pagination-item')
      }
      if (this.disabled) {
        classList.push('disabled')
      }
      if (this.innerCurrent === page) {
        classList.push('selected')
      }
      return {
        classList,
        isEllipsis: !!ellipsis,
        page
      }
    },
    setCurrent (page) {
      if (page < 1) {
        page = 1
      }
      if (page > this.innerTotalPage) {
        page = this.innerTotalPage
      }
      if (this.innerCurrent !== page) {
        this.innerCurrent = page
        this.$emit('update:current', page)
        this.$emit('on-change', page)
      }
    },
    handleClickPrevious () {
      if (this.innerCurrent <= 1 || this.disabled) {
        return
      }
      this.setCurrent(this.innerCurrent - 1)
    },
    handleClickNext () {
      if (this.innerCurrent >= this.innerTotalPage || this.disabled) {
        return
      }
      this.setCurrent(this.innerCurrent + 1)
    },
    handleClickPage (item) {
      if (this.disabled) {
        return
      }
      if (item.isEllipsis) {
        if (item.page === -1) {
          this.setCurrent(this.innerCurrent - this.displayPageCount)
          return
        } else {
          this.setCurrent(this.innerCurrent + this.displayPageCount)
          return
        }
      }
      this.setCurrent(item.page)
    },
    handleJumpToPage () {
      let page = this.elevatorValue * 1
      if (page) {
        page = Math.round(page)
        this.setCurrent(page)
      }
      this.elevatorValue = ''
    }
  }
}
</script>
